import { useEffect, useRef, useState } from "react";

/**
 * True once the element has entered (or come near) the viewport, and it stays
 * true from then on.
 *
 * Used to defer per-card network work. A library grid renders up to 300 tiles;
 * firing a TMDB lookup on mount for every artwork-less one would be a burst of
 * requests for posters the user may never scroll to. `rootMargin` starts the
 * work slightly before the tile is visible so the poster is usually resolved
 * by the time it scrolls into view.
 */
export function useInView<T extends HTMLElement>(rootMargin = "200px"): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView) return;
    const el = ref.current;
    if (!el) return;
    // Environments without IntersectionObserver (jsdom under the test runner)
    // would never fire, leaving the deferred work permanently disabled. Treat
    // them as visible so the degradation is "load it" rather than "never load".
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView, rootMargin]);

  return [ref, inView];
}
